import React, { useState, useCallback, useEffect } from 'react'
import { Tabs, Collapse, Space, Row, Col, Card, Typography, Pagination, Form, Button, Input, Select, message, Modal, notification } from "antd"
import { CaretRightOutlined } from '@ant-design/icons'
import { startCase, snakeCase } from "lodash"
import axios from "axios";

import PageHeader from '../Layout/PageHeader'
import PricingPackageLabelList from './PricingPackageLabelList';

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { Text } = Typography;

const auditInfoLabel = {
  updatedBy: "Last Updated By",
  updatedAt: "Last Updated At",
  action: "Action",
}

const CATEGORIES = [
  { key: "domestic", label: "Domestic Postage" },
  { key: "international", label: "International Postage" },
  { key: "courier", label: "Domestic Courier" },
  { key: "internationalCourier", label: "International Courier" },
  { key: "fee", label: "Fee" },
]

const PricingPackageLabels = () => {
  const { permissions } = usePermissions();

  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState(CATEGORIES[0].key)
  const [namesFilter, setNamesFilter] = useState([])
  const [loading, setLoading] = useState(false)
  const [labels, setLabels] = useState([])
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 })
  const [isAuditLoading, setIsAuditLoading] = useState(false)
  const [auditInfo, setAuditInfo] = useState(null)
  const [nameOptions, setNameOptions] = useState([])

  const activeCategoryLabel = CATEGORIES.find(category => category.key === activeTab)?.label || startCase(activeTab)

  const fetchNameOptions = useCallback(async () => {
    const res = await axios.get("/api/pricing-package-labels", {
      params: { category: activeTab, limit: 1000 },
    })

    setNameOptions((res.data.data || []).map((label) => label.name))
  }, [activeTab])

  const fetchLabels = useCallback(async () => {
    setLoading(true)

    const res = await axios.get("/api/pricing-package-labels", {
      params: {
        category: activeTab,
        names: namesFilter.length ? namesFilter.join(",") : undefined,
        page: pagination.currentPage,
        limit: pagination.limit,
      },
    })

    setLabels(res.data.data || [])
    setPagination(prevPagination => ({ ...prevPagination, ...res.data.pagination }))

    setLoading(false)
  }, [activeTab, namesFilter, pagination.currentPage, pagination.limit])

  const fetchLatestAudit = useCallback(async () => {
    setIsAuditLoading(true)
    try {
      const res = await axios.get("/api/admin-audit-logs", {
        params: {
          subjectType: "PricingPackageLabel",
          auditType: "ADMIN",
          limit: 1,
        },
      })
      const auditLogs = res.data.data || []
      if (auditLogs.length > 0) {
        const latestAudit = auditLogs[0]
        setAuditInfo({
          updatedBy: latestAudit.updatedBy?.email || latestAudit.updatedBy?.username || "Unknown",
          updatedAt: new Date(latestAudit.createdAt).toLocaleString(),
          action: startCase(latestAudit.description),
        })
      } else {
        setAuditInfo({
          updatedBy: "N/A",
          updatedAt: "N/A",
          action: "No changes recorded yet",
        })
      }
    } catch (error) {
      message.error("Failed to fetch audit history")
      console.error(error)
    } finally {
      setIsAuditLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLabels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, namesFilter, pagination.currentPage, pagination.limit])

  useEffect(() => {
    fetchNameOptions()
  }, [fetchNameOptions])

  useEffect(() => {
    fetchLatestAudit()
  }, [fetchLatestAudit])

  const changeTab = (tab) => {
    setActiveTab(tab)
    setNamesFilter([])
    setPagination(prevPagination => ({ ...prevPagination, currentPage: 1 }))
  }

  const namesFilterHandler = (values) => {
    setNamesFilter(values)
    setPagination(prevPagination => ({ ...prevPagination, currentPage: 1 }))
  }

  const notifyUploadRequired = (name) => {
    notification.info({
      message: 'Upload required',
      description: `"${name}" won't show pricing/cost data until you upload the ${activeCategoryLabel} Pricing file with a matching sheet name for this package.`,
      duration: 0,
      style: { width: 500 },
    })
  }

  const createHandler = async (values) => {
    try {
      await axios.post('/api/pricing-package-labels', {
        name: values.name,
        category: activeTab,
      })

      message.success(`Package name added.`)
      notifyUploadRequired(values.name)
      form.resetFields()
      fetchLabels()
      fetchLatestAudit()
      fetchNameOptions()
    } catch (error) {
      message.error(error.response?.data?.message || error.message)
    }
  }

  const setDefaultHandler = (labelId) => {
    const label = labels.find((label) => label.objectId === labelId)

    const messageContent = <p>
      Are you sure you want to set <strong>{label?.name}</strong> as the default {activeTab} package?<br />
      Merchants without an explicit package assignment will use this pricing.
    </p>

    Modal.confirm({
      content: messageContent,
      title: `Change Default Package?`,
      async onOk() {
        try {
          await axios.patch(`/api/pricing-package-labels/${labelId}/default`)
          message.success(`Default package updated.`)
          fetchLabels()
          fetchLatestAudit()
        } catch (error) {
          message.error(error.response?.data?.message || error.message)
        }
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Pricing Package Names"
        childRoutes={[{ path: "/package/definitions", breadcrumbName: "Pricing Package Names" }]}
      />

      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        {permissions?.includes(PERMISSIONS.CREATE_PRICING_PACKAGE_LABEL) && (
          <div className="create-pricing-package-label-container">
            <Collapse
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
            >
              <Collapse.Panel header={`Add New ${activeCategoryLabel} Package Name`}>
                <Form
                  form={form}
                  name="new-pricing-package-label-form"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  autoComplete="off"
                  onFinish={createHandler}
                >
                  <p>
                    <Text type="secondary">
                      This will create a package name under the <Text strong>{activeCategoryLabel}</Text> category
                      (the tab currently selected below). Switch tabs first if you meant a different category.
                    </Text>
                  </p>

                  <Form.Item
                    extra={`Must exactly match the Excel sheet name used for this category's pricing upload.`}
                    label="Name"
                    name="name"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item wrapperCol={{ offset: 6, span: 16 }} shouldUpdate>
                    {() => (
                      <Text type="secondary">
                        Key: <Text code>{snakeCase(form.getFieldValue('name') || '')}</Text>
                      </Text>
                    )}
                  </Form.Item>

                  <Form.Item wrapperCol={{ offset: 6, span: 16 }}>
                    <Button type="primary" htmlType="submit">
                      Add
                    </Button>
                  </Form.Item>
                </Form>
              </Collapse.Panel>
            </Collapse>
          </div>
        )}

        {permissions?.includes(PERMISSIONS.VIEW_ADMIN_AUDIT_LOGS)
          && !isAuditLoading
          && auditInfo
          && (
          <Card>
            {Object.entries(auditInfoLabel).map(([key, label]) => (
              <Row key={key}>
                <span>
                  <Text strong>{label}:{` `} </Text> {auditInfo[key]}
                </span>
              </Row>
            ))}
          </Card>
        )}

        <div style={{ background: "#fafafa", padding: 16, borderRadius: 8 }}>
          <Tabs activeKey={activeTab} animated onTabClick={changeTab}>
            {CATEGORIES.map(category => (
              <Tabs.TabPane tab={category.label} key={category.key} />
            ))}
          </Tabs>

          <Collapse
            style={{ marginTop: 16 }}
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            <Collapse.Panel header="Filters" key="filters">
              <Row gutter={12} align="middle">
                <Col>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Filter by name"
                    style={{ width: 300 }}
                    value={namesFilter}
                    onChange={namesFilterHandler}
                    optionFilterProp="children"
                    maxTagCount="responsive"
                  >
                    {nameOptions.map((name) => (
                      <Select.Option key={name} value={name}>{name}</Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>

          <Row justify="end" align="middle" style={{ marginTop: 16 }}>
            <Col>
              <Pagination
                total={pagination?.totalSize}
                pageSize={pagination?.limit}
                onChange={(currentPage, limit) => setPagination(prevPagination => ({ ...prevPagination, limit, currentPage }))}
                current={pagination.currentPage}
              />
            </Col>
          </Row>

          <div style={{ marginTop: 8 }}>
            <PricingPackageLabelList
              labels={labels}
              loading={loading}
              onSetDefault={setDefaultHandler}
            />
          </div>
        </div>
      </Space>
    </div>
  )
}

export default PricingPackageLabels
